import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { format } from 'date-fns';
import StatisticsList from './StatisticsList';
import Spinner from '@dashboard/components/Spinner/Spinner';
import { recentSubmissionsKey } from '@dashboard/lib/queryKeys';
import useSubmissionQueries from '@dashboard/hooks/queries/useSubmissionQueries';

function useRecentSubmissions() {
  const { sellerId } = useAuth();

  const { getSubmissions } = useSubmissionQueries();

  const { data, isError, isLoading } = useQuery({
    queryKey: recentSubmissionsKey(sellerId),
    queryFn: () =>
      getSubmissions(sellerId, {
        limit: 4,
        livemode: true,
        status: 'succeeded',
      }),
    staleTime: 1000 * 60, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  return {
    isLoading,
    isError,
    data,
  };
}

export default function RecentSubmissions() {
  const { data, isLoading, isError } = useRecentSubmissions();

  return (
    <StatisticsList.List>
      {isLoading && (
        <div>
          <Spinner />
        </div>
      )}

      {isError && <StatisticsList.EmptyState>Something went wrong</StatisticsList.EmptyState>}

      {!data?.length && <StatisticsList.EmptyState>No submissions yet</StatisticsList.EmptyState>}

      {data?.map(submission => (
        <StatisticsList.Item key={submission._id} href={`/submissions/${submission._id}`}>
          <StatisticsList.Left>
            <StatisticsList.PrimaryText>{submission.customerEmail}</StatisticsList.PrimaryText>
            <StatisticsList.SecondaryText>
              {submission.page.title || 'Untitled form'}
            </StatisticsList.SecondaryText>
          </StatisticsList.Left>

          <StatisticsList.Right>
            <StatisticsList.SecondaryText>
              {format(new Date(submission.createdAt), 'MMM d, yyyy HH:mm')}
            </StatisticsList.SecondaryText>
          </StatisticsList.Right>
        </StatisticsList.Item>
      ))}
    </StatisticsList.List>
  );
}
